import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  forwardRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

/** Change event object emitted by MatCheckbox. */
export class BllCheckboxChange {
  /** The source MatCheckbox of the event. */
  source: BllChecboxComponent;
  /** The new `checked` value of the checkbox. */
  checked: boolean;
}

@Component({
  selector: 'bll-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // "[id]": 'id',
    class: 'bll-checkbox',
    '[attr.tabindex]': 'null',
    '[class.bll-checkbox-indeterminate]': 'indeterminate',
    '[class.bll-checkbox-checked]': 'checked',
    '[class.bll-checkbox-disabled]': 'disabled',
    '[class.bll-checkbox-inline]': 'inline',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BllChecboxComponent),
      multi: true,
    },
  ],
})
@Input()
export class BllChecboxComponent implements OnInit {
  /**
   * Attached to the aria-label attribute of the host element. In most cases, arial-labelledby will
   * take precedence so this may be omitted.
   */
  @Input('aria-label') ariaLabel: string = '';
  @Input('id') id: string = '';
  @Input() size: 'md' | 'lg' = 'md';

  /**
   * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
   */
  @Input('aria-labelledby') ariaLabelledby: string | null = null;

  private _state: boolean = false;

  /** Whether the checkbox is required. */
  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = value;
  }
  private _required: boolean;

  /**
   * Whether the checkbox is checked.
   */
  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: boolean) {
    if (value != this.checked) {
      this._checked = value;
      this._changeDetectorRef.markForCheck();
      this._emitChangeEvent();
    }
  }
  private _checked: boolean = false;

  @Input() theme: 'default' | 'slider';

  /**
   * Whether the checkbox is disabled. This fully overrides the implementation provided by
   * mixinDisabled, but the mixin is still required because mixinTabIndex requires it.
   */
  @Input()
  get disabled(): any {
    return this._disabled;
  }
  set disabled(value: any) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this.disabled) {
      this._disabled = newValue;
      this._changeDetectorRef.markForCheck();
    }
  }
  private _disabled: boolean = false;

  @Input()
  get indeterminate(): any {
    return this._indeterminate;
  }
  set indeterminate(value: any) {
    const changed = value != this._indeterminate;
    this._indeterminate = coerceBooleanProperty(value);
    if (changed) {
      if (value === false) this.checked = false;
      this.indeterminateChange.emit(this._indeterminate);
      this._changeDetectorRef.markForCheck();
    }
  }
  private _indeterminate: boolean = false;

  @Input()
  get error(): any {
    return this._error;
  }
  set error(value: any) {
    this._error = coerceBooleanProperty(value);
    this._changeDetectorRef.markForCheck();
  }
  private _error: boolean;

  @Input()
  get inline(): any {
    return this._inline;
  }
  set inline(value: any) {
    this._inline = coerceBooleanProperty(value);
    this._changeDetectorRef.markForCheck();
  }
  private _inline: boolean;
  /** Name value will be applied to the input element if present */
  @Input() name: string | null = null;

  @Output() readonly change: EventEmitter<BllCheckboxChange> = new EventEmitter<BllCheckboxChange>();

  /** Name value will be applied to the input element if present */
  @Input() tabindex: number | null = null;

  /** The value attribute of the native input element */
  @Input() value: string;

  @ViewChild('input') _inputElement: ElementRef<HTMLInputElement>;

  @Output() stateChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() indeterminateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private _changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit() {}

  /**
   * Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor.
   * @docs-private
   */
  _onTouched: () => any = () => {};
  private _controlValueAccessorChangeFn: (value: any) => void = () => {};

  _change(): void {
    this._state = !this._state;
    this.stateChange.emit(this._state);
  }

  /**
   * Invoked when the model has been changed
   */
  onChange: (_: any) => void = (_: any) => {};

  /**
   * Invoked when the model has been touched
   */
  onTouched: () => void = () => {};

  /**
   * Method that is invoked on an update of a model.
   */
  updateChanges() {
    this.onChange(this._state);
  }

  ///////////////
  // OVERRIDES //
  ///////////////

  /**
   * Writes a new item to the element.
   * @param value the value
   */

  writeValue(value: any) {
    this.checked = !!value;
  }
  // Implemented as part of ControlValueAccessor.
  registerOnChange(fn: (value: any) => void) {
    this._controlValueAccessorChangeFn = fn;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(fn: any) {
    this._onTouched = fn;
  }

  // Implemented as part of ControlValueAccessor.
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  _getAriaChecked(): 'true' | 'false' | 'mixed' {
    return this.checked ? 'true' : this.indeterminate ? 'mixed' : 'false';
  }

  private _emitChangeEvent() {
    const event = new BllCheckboxChange();
    event.source = this;
    event.checked = this.checked;

    this._controlValueAccessorChangeFn(this.checked);
    this.change.emit(event);
  }

  /** Toggles the `checked` state of the checkbox. */
  toggle(): void {
    this.checked = !this.checked;
  }

  /**
   * Event handler for checkbox input element.
   * Toggles checked state if element is not disabled.
   * Do not toggle on (change) event since IE doesn't fire change event when
   *   indeterminate checkbox is clicked.
   * @param event
   */
  _onInputClick(event: Event) {
    // We have to stop propagation for click events on the visual hidden input element.
    // By default, when a user clicks on a label element, a generated click event will be
    // dispatched on the associated input element. Since we are using a label element as our
    // root container, the click event on the `checkbox` will be executed twice.
    // The real click event will bubble up, and the generated click event also tries to bubble up.
    // This will lead to multiple click events.
    // Preventing bubbling for the second event will solve that issue.
    event.stopPropagation();

    // If resetIndeterminate is false, and the current state is indeterminate, do nothing on click
    if (!this.disabled) {
      if (this.indeterminate) {
        Promise.resolve().then(() => {
          this.indeterminate = false;
        });
      }
      if (!this.indeterminate) {
        this.toggle();
      }
      // Emit our custom change event if the native input emitted one.
      // It is important to only emit it, if the native input triggered one, because
      // we don't want to trigger a change event, when the `checked` variable changes for example.
      this._emitChangeEvent();
    } else {
      this._inputElement.nativeElement.checked = this.checked;
      this._inputElement.nativeElement.indeterminate = this.indeterminate;
    }
  }

  /** Method being called whenver the label changes */
  _onLabelTextChange() {
    this._changeDetectorRef.detectChanges();
  }

  _onInteractionEvent(event: Event) {
    // We always have to stop propagation on the change event.
    // Otherwise the change event, from the input element, will bubble up and
    // emit its event object to the `change` output.
    event.stopPropagation();
  }
}
